/**
 * Intents page rules (plan B1, spec §4). Import-free so `node --test` loads it directly. The server enforces the
 * same rules (backend intent-rules.ts); these only decide what the UI offers.
 */
export type PhraseSource = 'rasa' | 'operator' | 'review';

export const SOURCE_LABEL: Record<PhraseSource, string> = {
  rasa: 'synced',
  operator: 'hand-written',
  review: 'from review',
};

export const CREATE_HINT = 'v1 intents are created in chatbot';
export const SYNCED_HINT = 'Synced from chatbot — change it in chatbot';

export function canCreateIntent(system: string): boolean {
  return system !== 'chatbot';
}

export function intentLocks(i: { synced: boolean }): { name: boolean; delete: boolean } {
  return { name: i.synced, delete: i.synced };
}

export function canChangePhrase(t: { source?: string }): boolean {
  return t.source !== 'rasa';
}

export function filterPhrases<T extends { source?: string }>(texts: T[], f: PhraseSource | 'all'): T[] {
  if (f === 'all') return texts;
  return texts.filter((t) => (t.source ?? 'operator') === f);
}

/** Promotion to live needs a completed, failure-free last run (the server checks the same). */
export function canPromote(r: { state: string; status: string; failed: number; lastSyncedAt: string | null }): boolean {
  return r.state !== 'live' && r.status === 'succeeded' && r.failed === 0 && !!r.lastSyncedAt;
}

export function importBadge(
  s: { state: string; status: string } | null,
): { label: string; tone: 'gray' | 'amber' | 'green' | 'red' } | null {
  if (!s) return null;
  if (s.status === 'failed' && s.state === 'importing') return { label: 'Import failed', tone: 'red' };
  if (s.state === 'importing') return { label: 'Importing…', tone: 'gray' };
  return { label: `Imported · ${s.state}`, tone: s.state === 'live' ? 'green' : 'amber' };
}
